import React, { useState, useEffect } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Box,
    Typography,
    Grid2,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Timeline as TimelineIcon,
    TrendingUp as TrendingUpIcon,
    AccountBalanceWallet as WalletIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { FeeCollectionEvent } from '../../types/DashboardTypes';

interface FeeHistoryChartProps {
    dexType: 'uniswap' | 'aerodrome';
    contractAddress?: string;
}

const FeeHistoryChart: React.FC<FeeHistoryChartProps> = ({
    dexType,
    contractAddress
}) => {
    const [events, setEvents] = useState<FeeCollectionEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Mock data loading - replace with actual event queries
    useEffect(() => {
        const loadFeeHistory = async () => {
            try {
                setLoading(true);

                // Simulate API call to get fee collection events
                await new Promise(resolve => setTimeout(resolve, 1000));

                // Mock fee collection events
                const mockEvents: FeeCollectionEvent[] = [
                    {
                        id: '1',
                        timestamp: Date.now() - 86400000, // 1 day ago
                        pool: dexType === 'aerodrome' ? '0x1234...5678' : undefined,
                        tokenId: dexType === 'uniswap' ? '1' : undefined,
                        feesCollected0: '12000000000000000', // 0.012 ETH
                        feesCollected1: '3200000000000000000000', // 3200 MEMER
                        liquidityAdded: '1500000000000000000', // 1.5 LP tokens
                        wasReinvested: true,
                        triggeredBy: '0xabcd...efgh',
                        gasUsed: '180000',
                        gasPrice: '20000000000' // 20 gwei
                    },
                    {
                        id: '2',
                        timestamp: Date.now() - 172800000, // 2 days ago
                        pool: dexType === 'aerodrome' ? '0x1234...5678' : undefined,
                        tokenId: dexType === 'uniswap' ? '1' : undefined,
                        feesCollected0: '8500000000000000', // 0.0085 ETH
                        feesCollected1: '2100000000000000000000', // 2100 MEMER
                        liquidityAdded: '950000000000000000', // 0.95 LP tokens
                        wasReinvested: true,
                        triggeredBy: '0x9876...5432',
                        gasUsed: '175000',
                        gasPrice: '18000000000' // 18 gwei
                    },
                    {
                        id: '3',
                        timestamp: Date.now() - 345600000, // 4 days ago
                        pool: dexType === 'aerodrome' ? '0x1234...5678' : undefined,
                        tokenId: dexType === 'uniswap' ? '1' : undefined,
                        feesCollected0: '15000000000000000', // 0.015 ETH
                        feesCollected1: '4000000000000000000000', // 4000 MEMER
                        wasReinvested: false,
                        triggeredBy: '0x1111...2222',
                        gasUsed: '120000',
                        gasPrice: '22000000000' // 22 gwei
                    }
                ];

                setEvents(mockEvents);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load fee history');
            } finally {
                setLoading(false);
            }
        };

        if (contractAddress) {
            loadFeeHistory();
        }
    }, [contractAddress, dexType]);

    const formatTokenAmount = (amount: string, decimals: number = 18) => {
        const value = parseFloat(amount) / Math.pow(10, decimals);
        return value.toFixed(6);
    };

    const formatAddress = (address: string) => {
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    };

    const calculateGasCost = (gasUsed: string, gasPrice: string) => {
        const cost = (parseFloat(gasUsed) * parseFloat(gasPrice)) / 1e18;
        return cost.toFixed(6);
    };

    const getTotalFeesCollected = () => {
        return events.reduce((total, event) => {
            const eth = parseFloat(formatTokenAmount(event.feesCollected0));
            return total + eth;
        }, 0);
    };

    const getTotalReinvested = () => {
        return events.filter(e => e.wasReinvested).length;
    };

    if (loading) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                        <Typography variant="body1" sx={{ ml: 2 }}>
                            Loading fee history...
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Alert severity="error">
                        Error loading fee history: {error}
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={2}>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" gap={1}>
                        <TimelineIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            Fee Collection History
                        </Typography>
                    </Box>
                }
                subheader={`Recent fee collection and reinvestment activity`}
            />
            <CardContent>
                {/* Summary Stats */}
                <Grid2 container spacing={2} sx={{ mb: 3 }}>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.08)', borderRadius: 1 }}>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                                {getTotalFeesCollected().toFixed(4)} ETH
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Total Fees Collected
                            </Typography>
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1 }}>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                {events.length}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Collection Events
                            </Typography>
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.08)', borderRadius: 1 }}>
                            <Typography variant="h6" fontWeight="bold" color="secondary.main">
                                {getTotalReinvested()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Auto-Reinvested
                            </Typography>
                        </Box>
                    </Grid2>
                </Grid2>

                {/* Events Table */}
                {events.length > 0 ? (
                    <TableContainer component={Paper} elevation={1}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>{dexType === 'uniswap' ? 'Position' : 'Pool'}</TableCell>
                                    <TableCell align="right">WETH Fees</TableCell>
                                    <TableCell align="right">MEMER Fees</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                    <TableCell align="right">Gas Cost</TableCell>
                                    <TableCell>Triggered By</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map((event) => (
                                    <TableRow key={event.id} hover>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {new Date(event.timestamp).toLocaleDateString()}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {new Date(event.timestamp).toLocaleTimeString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace">
                                                {dexType === 'uniswap'
                                                    ? `#${event.tokenId}`
                                                    : formatAddress(event.pool || '')
                                                }
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatTokenAmount(event.feesCollected0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatTokenAmount(event.feesCollected1)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                icon={event.wasReinvested ? <TrendingUpIcon /> : <WalletIcon />}
                                                label={event.wasReinvested ? 'Reinvested' : 'Collected'}
                                                color={event.wasReinvested ? 'success' : 'primary'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {calculateGasCost(event.gasUsed, event.gasPrice)} ETH
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <PersonIcon fontSize="small" color="action" />
                                                <Typography variant="body2" fontFamily="monospace">
                                                    {formatAddress(event.triggeredBy)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Alert severity="info">
                        No fee collection events found. Fees will appear here once collection begins.
                    </Alert>
                )}

                {/* Additional Info */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="caption" color="textSecondary">
                        💡 Fee collection events are automatically tracked and displayed here.
                        Reinvested fees compound your {dexType === 'uniswap' ? 'position' : 'pool'} for exponential growth.
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

export default FeeHistoryChart;
